import type {
  MentionableEntity,
  MentionableGroup,
  MentionableUser,
} from "@/types";

export type MentionTokenType = "user" | "group";

export interface MentionTokenRef {
  type: MentionTokenType;
  id: number;
  start: number;
  end: number;
}

export type MentionSegment =
  | { type: "text"; value: string }
  | { type: "mention"; value: string; entity?: MentionableEntity };

const TOKEN_PATTERN = "@\\[(user|group):(\\d+)\\]";

export const escapeMentionPattern = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const mentionHandleFor = (entity: MentionableEntity) =>
  entity.type === "user" ? entity.email : entity.name;

export const mentionTokenFor = (entity: MentionableEntity) =>
  `@[${entity.type}:${entity.id}]`;

const tokenRegex = () => new RegExp(TOKEN_PATTERN, "gi");

const normalizeMentionId = (value: number) =>
  Number.isInteger(value) && value > 0 ? value : null;

const uniqueMentionEntities = (entities: MentionableEntity[]) => {
  const unique = new Map<string, MentionableEntity>();

  entities.forEach((entity) => {
    unique.set(`${entity.type}:${entity.id}`, entity);
  });

  return Array.from(unique.values());
};

const mentionLookupByType = (mentionables: MentionableEntity[]) => {
  const users = new Map<number, MentionableUser>();
  const groups = new Map<number, MentionableGroup>();

  mentionables.forEach((entity) => {
    if (entity.type === "user") {
      users.set(entity.id, entity);
    } else {
      groups.set(entity.id, entity);
    }
  });

  return { users, groups };
};

const resolveTokenEntity = (
  token: Pick<MentionTokenRef, "type" | "id">,
  mentionables: MentionableEntity[],
) => {
  const lookup = mentionLookupByType(mentionables);

  if (token.type === "user") {
    return lookup.users.get(token.id);
  }

  return lookup.groups.get(token.id);
};

const mentionRegexForHandle = (handle: string) => {
  const escapedHandle = escapeMentionPattern(handle.trim());
  if (!escapedHandle) return null;

  return new RegExp(
    `(^|\\s|\\(|\\[|\\{)@${escapedHandle}(?=$|\\s|\\)|\\]|\\}|[.,!?;:])`,
    "i",
  );
};

const legacyMentionEntities = (
  text: string,
  mentionables: MentionableEntity[],
) => {
  if (!text.includes("@")) return [];

  const found = mentionables.filter((entity) => {
    const regex = mentionRegexForHandle(mentionHandleFor(entity));
    return regex ? regex.test(text) : false;
  });

  return uniqueMentionEntities(found);
};

const mentionLabel = (entity: MentionableEntity) => `@${entity.name}`;

const fallbackTokenLabel = (token: Pick<MentionTokenRef, "type" | "id">) =>
  token.type === "user" ? "@usuario" : "@grupo";

const legacyMentionSegments = (
  text: string,
  mentionables: MentionableEntity[],
): MentionSegment[] => {
  if (!text.includes("@") || mentionables.length === 0) {
    return [{ type: "text", value: text }];
  }

  const entitiesByKey = new Map<string, MentionableEntity>();
  const keyCounts = new Map<string, number>();

  mentionables.forEach((entity) => {
    const keys = [entity.type === "user" ? entity.email : null, entity.name]
      .filter((key): key is string => Boolean(key?.trim()))
      .map((key) => key.trim().toLowerCase());

    keys.forEach((key) => {
      keyCounts.set(key, (keyCounts.get(key) || 0) + 1);
      if (!entitiesByKey.has(key)) {
        entitiesByKey.set(key, entity);
      }
    });
  });

  const uniqueKeys = Array.from(keyCounts.entries())
    .filter(([, count]) => count === 1)
    .map(([key]) => key)
    .sort((a, b) => b.length - a.length);

  if (uniqueKeys.length === 0) {
    return [{ type: "text", value: text }];
  }

  const escaped = uniqueKeys.map((key) => escapeMentionPattern(key));
  const pattern = new RegExp(
    `(^|[\\s([{])@(${escaped.join("|")})(?=$|[\\s)\\]}.,!?;:])`,
    "gi",
  );

  const result: MentionSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }

    if (match[1]) {
      result.push({ type: "text", value: match[1] });
    }

    const entity = entitiesByKey.get(match[2].toLowerCase());
    if (entity) {
      result.push({ type: "mention", value: mentionLabel(entity), entity });
    } else {
      result.push({ type: "text", value: match[0] });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    result.push({ type: "text", value: text.slice(lastIndex) });
  }

  return result.length > 0 ? result : [{ type: "text", value: text }];
};

export const mentionTokensFromText = (text: string): MentionTokenRef[] => {
  const matches = text.matchAll(tokenRegex());
  const tokens: MentionTokenRef[] = [];

  for (const match of matches) {
    const type = match[1] as MentionTokenType;
    const id = normalizeMentionId(Number.parseInt(match[2], 10));
    const index = match.index ?? -1;

    if (id === null || index < 0) continue;

    tokens.push({
      type,
      id,
      start: index,
      end: index + match[0].length,
    });
  }

  return tokens;
};

export const mentionedEntitiesFromText = (
  text: string,
  mentionables: MentionableEntity[],
) => {
  const tokenEntities = mentionTokensFromText(text)
    .map((token) => resolveTokenEntity(token, mentionables))
    .filter((entity): entity is MentionableEntity => Boolean(entity));

  const legacyEntities = legacyMentionEntities(text, mentionables);

  return uniqueMentionEntities([...tokenEntities, ...legacyEntities]);
};

export const mentionedIdsFromText = (
  text: string,
  mentionables: MentionableEntity[],
) => {
  const tokenUserIds = new Set<number>();
  const tokenGroupIds = new Set<number>();

  mentionTokensFromText(text).forEach((token) => {
    if (token.type === "user") {
      tokenUserIds.add(token.id);
    } else {
      tokenGroupIds.add(token.id);
    }
  });

  const entities = mentionedEntitiesFromText(text, mentionables);

  entities.forEach((entity) => {
    if (entity.type === "user") {
      tokenUserIds.add(entity.id);
    } else {
      tokenGroupIds.add(entity.id);
    }
  });

  return {
    userIds: Array.from(tokenUserIds),
    groupIds: Array.from(tokenGroupIds),
  };
};

export const mentionSegmentsFromText = (
  text: string,
  mentionables: MentionableEntity[],
): MentionSegment[] => {
  const tokens = mentionTokensFromText(text);
  if (tokens.length === 0) {
    return legacyMentionSegments(text, mentionables);
  }

  const segments: MentionSegment[] = [];
  let cursor = 0;

  tokens.forEach((token) => {
    if (token.start > cursor) {
      const plainSlice = text.slice(cursor, token.start);
      segments.push(...legacyMentionSegments(plainSlice, mentionables));
    }

    const entity = resolveTokenEntity(token, mentionables);
    segments.push({
      type: "mention",
      value: entity ? mentionLabel(entity) : fallbackTokenLabel(token),
      ...(entity ? { entity } : {}),
    });

    cursor = token.end;
  });

  if (cursor < text.length) {
    const trailing = text.slice(cursor);
    segments.push(...legacyMentionSegments(trailing, mentionables));
  }

  return segments.length > 0 ? segments : [{ type: "text", value: text }];
};

export const mentionTextToDisplayText = (
  text: string,
  mentionables: MentionableEntity[] = [],
) =>
  mentionSegmentsFromText(text, mentionables)
    .map((segment) => segment.value)
    .join("");
